/* Native Shopify cart + anonymous attribution bridge. No hardcoded prices/SKUs.
 * Cart attributes preserve order linkage; only read_orders is required downstream.
 * Accelerated checkout is intentionally not injected until linkage acceptance. */
(() => {
  'use strict';
  if (window.NimoCommerce) return;
  let config = {};
  try { config = JSON.parse(document.getElementById('nimo-config')?.textContent || '{}'); } catch { return; }
  const ATTR_KEY = 'nimo_attribution_v3', ATTEMPT_KEY = 'nimo_checkout_ref_v3';
  const analytics = () => window.NimoAnalytics;
  const permitted = purpose => config.analyticsEnabled === true && analytics()?.configured?.() === true &&
    !window.Shopify?.designMode && analytics()?.allowed(purpose) === true;
  const label = value => analytics()?.label(value) || '';
  // Commercial selection metadata is not visitor tracking. Keep the chosen
  // tier/SKU/phase on the Shopify line item even when analytics consent is
  // denied or the analytics bridge is unavailable; only the attribution
  // fields below are gated by consent.
  const businessValue = value => value == null ? '' : String(value).trim().slice(0, 256);
  const validTiers = new Set(['plano','single-vision','progressive']);
  const normalizeTier = value => {
    const key = businessValue(value).toLowerCase().replace(/_/g, '-');
    const canonical = ({'tier-plano': 'plano', 'tier-single-vision': 'single-vision',
      'tier-progressive': 'progressive', singlevision: 'single-vision', single: 'single-vision'}[key] || key);
    return validTiers.has(canonical) ? canonical : '';
  };
  const emit = (event, parameters) => analytics()?.emit(event, parameters);
  const busy = new WeakSet(), partialAdds = new WeakMap(), directPending = new Map();
  function storageGet(key) { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } }
  function storageSet(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* optional storage */ } }
  function captureAttribution() {
    if (!permitted('marketing') || window.Shopify?.designMode) return {};
    const query = new URLSearchParams(window.location.search), touch = {};
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
      const value = label(query.get(key)); if (value) touch[key] = value;
    }
    // Agency must set these from ad URL macros. Never use raw fbclid/gclid as attribution IDs.
    for (const [param, key] of [['campaign_id', 'attr_campaign_id'], ['adset_id', 'attr_adset_id'],
      ['ad_id', 'attr_ad_id'], ['placement', 'attr_placement']]) {
      const value = label(query.get(param)); if (value) touch[key] = value;
    }
    const paid = ['fbclid', 'gclid', 'msclkid', 'ttclid'].some(key => query.has(key));
    if (Object.keys(touch).length) {
      storageSet(ATTR_KEY, {touch, at: Date.now()});
      emit('nimo_attribution', {...touch, has_paid_click_id: paid,
        attr_latest_channel: touch.utm_source || '', attr_latest_ad_id:touch.attr_ad_id || '',
        attr_latest_placement:touch.attr_placement || ''});
    }
    const saved = storageGet(ATTR_KEY);
    return saved && Date.now() - saved.at < 30 * 60 * 1000 ? saved.touch : {};
  }
  function reference() {
    if (!permitted('analytics')) return '';
    const saved = storageGet(ATTEMPT_KEY);
    if (saved && Date.now() - saved.at < 30 * 60 * 1000 && /^[a-f0-9-]{36}$/i.test(saved.id)) return saved.id;
    const id = window.crypto.randomUUID(); storageSet(ATTEMPT_KEY, {id, at: Date.now()}); return id;
  }
  function selectedMetadata(form) {
    const tier = form?.querySelector('[data-nimo-tier]:checked');
    const nativeOption = form?.querySelector('[data-nimo-variant]')?.selectedOptions[0];
    const previous = storageGet('nimo_selection_v3') || {};
    // A present native SKU, including an intentionally blank SKU, is authoritative.
    // Never carry a previous product's SKU/tier into a new Product Object form.
    const nativeSku = nativeOption?.getAttribute('data-nimo-sku');
    const selected = {tier: normalizeTier(tier?.dataset.nimoTier || form?.dataset.nimoTier || form?.elements.namedItem('properties[_nimo_lens_type]')?.value ||
        form?.elements.namedItem('properties[_nimo_tier]')?.value || (form ? '' : previous.tier)),
      sku: businessValue(nativeSku != null ? nativeSku : tier?.dataset.nimoSku || form?.dataset.nimoSku || form?.elements.namedItem('properties[_nimo_sku]')?.value || (form ? '' : previous.sku)),
      phase: businessValue(form?.dataset.nimoPhase || previous.phase)};
    if (form) storageSet('nimo_selection_v3', selected); // Business selection, not visitor tracking.
    return selected;
  }
  function attributes(ref, form) {
    const measured = permitted('analytics');
    // Explicit empty values clear a previous native cart's measurement cohort.
    // Disabled rollout, draft/unconfigured analytics or denied consent must not
    // label an otherwise valid business purchase as a measured V3 order.
    const attr = {nimo_tracking_version: measured ? '3' : '', nimo_source_site: 'nimoar.com',
      nimo_site_build_id: measured ? label(config.siteBuildId || 'shopify-theme-v3') : '',
      nimo_origin: measured ? 'shopify_theme' : '', nimo_checkout_ref: measured ? ref || '' : '',
      nimo_measurement_consent: measured ? 'allowed' : 'not_allowed'};
    const selected=selectedMetadata(form);
    attr.nimo_landing_tier = selected.tier; attr.nimo_landing_sku = selected.sku;
    attr.nimo_selected_tier = selected.tier;
    attr.nimo_offer_phase = selected.phase;
    const marketing = measured && permitted('marketing');
    const touch = marketing ? captureAttribution() : {};
    for (const key of ['utm_source','utm_medium','utm_campaign','utm_content','attr_campaign_id',
      'attr_adset_id','attr_ad_id','attr_placement']) attr[key] = marketing ? label(touch[key]) : '';
    return attr;
  }
  function route(name, fallback) {
    const path = config[name] || `${window.Shopify?.routes?.root || '/'}${fallback}`;
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Cross-origin cart route rejected');
    return url.pathname.endsWith('.js') ? url.href : `${url.href}.js`;
  }
  async function post(url, data) {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {method:'POST', credentials:'same-origin',
        headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify(data), signal:controller.signal});
      let json; try { json = await response.json(); } catch { throw new Error('Invalid cart response'); }
      if (!response.ok) {
        const error=new Error(json.description || json.message || 'Cart operation failed');
        error.definitelyRejected=[400,401,403,404,422].includes(response.status);
        const serverMessage=[json.description,json.message].find(value=>typeof value==='string' && value.trim());
        error.userMessage=serverMessage?.trim().slice(0,500) || '';
        throw error;
      }
      return json;
    } finally { clearTimeout(timeout); }
  }
  async function readCart() {
    const controller=new AbortController(),timeout=setTimeout(()=>controller.abort(),15000);
    try {
      const response=await fetch(route('cartReadUrl','cart'),{credentials:'same-origin',headers:{Accept:'application/json'},signal:controller.signal});
      if(!response.ok)throw new Error('Cart reconciliation unavailable');
      const cart=await response.json();
      if(!Array.isArray(cart.items))throw new Error('Invalid cart reconciliation response');
      return cart;
    } finally {clearTimeout(timeout);}
  }
  async function reconcileAdd(requestId) {
    const cart=await readCart();
    return cart.items.some(item=>item.properties?._nimo_add_request===requestId);
  }
  function formMessage(form, message) {
    const region = form.querySelector('[data-nimo-form-error]');
    if (region) { region.textContent = message; region.hidden = !message; }
  }
  async function directProductCheckout(form, variant) {
    busy.add(form);
    const button=form.querySelector('[type="submit"]'),originalDisabled=button?.disabled;
    directPending.set(form,originalDisabled);
    if (button) button.disabled=true;
    formMessage(form,'');
    let navigating=false;
    try {
      const fd=new FormData(form),quantity=Number(fd.get('quantity') ?? 1),properties={};
      if (!/^[1-9]\d{0,19}$/.test(variant) || quantity!==1) throw new Error('Invalid checkout selection');
      const root=String(window.Shopify?.routes?.root || '/');
      if (!/^\/(?:[a-z]{2,3}(?:-[a-z0-9]{2,8})*\/)?$/i.test(root)) throw new Error('Invalid locale route');
      const url=new URL(`${root}cart/${variant}:1`,window.location.origin);
      if (url.origin!==window.location.origin) throw new Error('Cross-origin checkout rejected');
      const fields=[...fd.entries()].filter(([key])=>/^properties\[[^\]]+\]$/.test(key));
      if (fields.length>25) throw new Error('Too many line item properties');
      for (const [key,value] of fields) {
        const property=key.slice(11,-1);
        if (['_nimo_order_type','_nimo_reservation_cohort','_nimo_lens_type'].includes(property)) properties[property]=businessValue(value);
      }
      const selected=selectedMetadata(form);
      let attr={nimo_source_site:'nimoar.com',nimo_measurement_consent:'not_allowed',nimo_selected_tier:selected.tier,
        nimo_landing_tier:selected.tier,nimo_landing_sku:selected.sku,nimo_offer_phase:selected.phase};
      try { attr=attributes(reference(),form); } catch {}
      properties._nimo_order_type=selected.phase;
      for (const [key,value] of Object.entries({_nimo_tier:selected.tier,_nimo_sku:selected.sku,
        _nimo_checkout_ref:attr.nimo_checkout_ref,_nimo_tracking_version:attr.nimo_tracking_version})) {
        if (value) properties[key]=businessValue(value);
      }
      if (Object.keys(properties).length>25) throw new Error('Too many line item properties');
      const bytes=new TextEncoder().encode(JSON.stringify(properties));
      let binary='';for (const byte of bytes) binary+=String.fromCharCode(byte);
      url.searchParams.set('properties',btoa(binary).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,''));
      const allowedAttributes=['nimo_tracking_version','nimo_source_site','nimo_site_build_id','nimo_origin','nimo_checkout_ref',
        'nimo_measurement_consent','nimo_landing_tier','nimo_landing_sku','nimo_selected_tier','nimo_offer_phase',
        'utm_source','utm_medium','utm_campaign','utm_content','attr_campaign_id','attr_adset_id','attr_ad_id','attr_placement'];
      for (const key of allowedAttributes) if (attr[key]) url.searchParams.set(`attributes[${key}]`,businessValue(attr[key]));
      if (attr.nimo_measurement_consent==='allowed') {
        try {
          emit('nimo_checkout_attempt',{checkout_attempt_id:attr.nimo_checkout_ref || '',tier_id:selected.tier,sku_id:selected.sku,
            shopify_variant_id:variant,offer_phase:selected.phase,checkout_transport:'cart_permalink',order_status:'unconfirmed'});
        } catch {}
        let timer;
        try { await Promise.race([Promise.resolve(analytics()?.flush?.()).catch(()=>{}),new Promise(resolve=>{timer=setTimeout(resolve,400);})]); }
        catch {} finally {clearTimeout(timer);}
      }
      window.location.assign(url.href);navigating=true;
    } catch {
      formMessage(form,config.unavailableText || 'Unable to open checkout. Please try again.');
    } finally {
      if (!navigating) {directPending.delete(form);busy.delete(form);if (button) button.disabled=originalDisabled;}
    }
  }
  // Native required bubbles otherwise follow the browser UI language rather
  // than the storefront locale. Only the deposit-consent message is authored
  // here; Shopify remains authoritative for server-side checkout validation.
  document.addEventListener('invalid', event => {
    const field = event.target;
    if (!field.matches?.('[data-nimo-required-message]')) return;
    const message = field.dataset.nimoRequiredMessage;
    if (field.validity.valueMissing && message) {
      field.setCustomValidity(message);
      const form = field.closest('[data-nimo-product-form]');
      if (form) formMessage(form, message);
    }
  }, true);
  document.addEventListener('change', event => {
    const field = event.target;
    if (!field.matches?.('[data-nimo-required-message]')) return;
    field.setCustomValidity('');
    const form = field.closest('[data-nimo-product-form]');
    if (form) formMessage(form, '');
  });
  function prop(form, key, value) {
    let field = form.elements.namedItem(`properties[${key}]`);
    if (!field) { field = document.createElement('input'); field.type='hidden'; field.name=`properties[${key}]`; form.append(field); }
    field.value=value;
  }
  function syncTierMetadata(form) {
    const tier = form?.querySelector('[data-nimo-tier]:checked');
    if (!tier) return;
    const normalized = normalizeTier(tier.dataset.nimoTier);
    prop(form, '_nimo_tier_label', businessValue(tier.dataset.nimoTierLabel || normalized || ''));
    prop(form, '_nimo_early_bird_price', businessValue(tier.dataset.nimoEarlyBirdPrice));
    prop(form, '_nimo_campaign_price', businessValue(tier.dataset.nimoCampaignPrice));
  }
  function variantChanged(select) {
    const form=select.closest('[data-nimo-product-form]'); if (!form) return;
    const option=select.selectedOptions[0], button=form.querySelector('[type="submit"]');
    const available=!!option && option.dataset.available === 'true';
    const price=form.querySelector('[data-nimo-price]');
    if (price && option?.dataset.priceFormatted) {
      const span=document.createElement('span');span.textContent=option.dataset.priceFormatted;price.replaceChildren(span);
      if (Number(option.dataset.comparePrice)>Number(option.dataset.price) && option.dataset.compareFormatted) {
        const compare=document.createElement('s');compare.textContent=option.dataset.compareFormatted;price.append(compare);
      }
    }
    if (button) {
      button.disabled=!available;
      if (!button.dataset.availableLabel) {
        const current=button.textContent.trim(), soldOut=config.soldOutText || 'Sold out';
        // Older cached markup can start on an unavailable ?variant=. Never
        // cache its Sold out state as the action label of an available variant.
        button.dataset.availableLabel=current && current!==soldOut ? current :
          (form.dataset.nimoPhase==='reservation_deposit' ? config.reserveText || 'Reserve' : config.addToCartText || 'Add to cart');
      }
      button.textContent=available ? button.dataset.availableLabel : (config.soldOutText || 'Sold out');
    }
  }
  async function add(form) {
    if (busy.has(form) || !form.reportValidity()) return;
    const select=form.querySelector('[data-nimo-variant]'), option=select?.selectedOptions[0];
    if (select && (!option || option.disabled || option.dataset.available !== 'true')) {
      formMessage(form, config.soldOutText || 'Sold out'); return;
    }
    const variant = form.elements.namedItem('id')?.value;
    if (!/^\d+$/.test(variant || '')) { formMessage(form, config.unavailableText || 'Select an available product first.'); return; }
    if (['reservation_balance','full_payment_preorder'].includes(form.dataset.nimoPhase)) {
      await directProductCheckout(form,variant); return;
    }
    busy.add(form); const button = form.querySelector('[type="submit"]');
    const originalDisabled = button?.disabled; if (button) button.disabled=true;
    formMessage(form, '');
    const ref = reference(), attr = attributes(ref, form);
    syncTierMetadata(form);
    const params = {checkout_attempt_id: attr.nimo_checkout_ref, tier_id: attr.nimo_landing_tier,
      sku_id: attr.nimo_landing_sku, shopify_variant_id: variant, offer_phase: attr.nimo_offer_phase};
    if (attr.nimo_offer_phase === 'reservation_deposit') emit('nimo_deposit_intent', params);
    prop(form, '_nimo_checkout_ref', attr.nimo_checkout_ref); prop(form, '_nimo_tier', attr.nimo_landing_tier);
    prop(form, '_nimo_sku', attr.nimo_landing_sku); prop(form, '_nimo_tracking_version',attr.nimo_tracking_version);
    try {
      const fd = new FormData(form), properties = {};
      for (const [key, value] of fd.entries()) {
        const match = key.match(/^properties\[([^\]]+)\]$/); if (match) properties[match[1]]=String(value);
      }
      const quantity = Number(fd.get('quantity') || 1);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) throw new Error('Invalid quantity');
      const signatureProperties={...properties};delete signatureProperties._nimo_checkout_ref;
      const signature = JSON.stringify({variant, quantity, properties:signatureProperties});
      let state=partialAdds.get(form);
      // A network timeout is NOT proof that Shopify rejected the add. Never blindly POST twice.
      if(state?.status==='unknown') {
        if(await reconcileAdd(state.requestId))state.status='added';
        else throw new Error('Ambiguous add outcome; inspect native cart before restarting');
      }
      if(state && state.signature!==signature)throw new Error('Inspect cart before changing a partially added item');
      if (!state) {
        state={signature,status:'unknown',requestId:window.crypto.randomUUID()};partialAdds.set(form,state);
        // Private functional retry marker; not a visitor identity and never sent to analytics.
        properties._nimo_add_request=state.requestId;
        try {
          await post(route('cartAddUrl','cart/add'), {items:[{id:variant, quantity, properties}]});
          state.status='added';
        } catch(error) {if(error.definitelyRejected)partialAdds.delete(form);throw error;}
      }
      // Linkage must succeed before continuing; no silent unlinked checkout on partial failure.
      await post(route('cartUpdateUrl','cart/update'), {attributes:attributes(permitted('analytics') ? reference() : '',form)});
      emit('nimo_cart_added', params);
      const directCheckout = form.dataset.nimoDirectCheckout === 'true' || form.dataset.nimoPhase === 'reservation_deposit';
      if (directCheckout) {
        emit('nimo_checkout_attempt', {...params, checkout_transport:'direct_checkout', order_status:'unconfirmed'});
        await analytics()?.flush();
      }
      window.location.assign(directCheckout ? `${window.Shopify?.routes?.root || '/'}checkout` : config.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`);
    } catch (error) {
      emit('nimo_cart_error', {offer_phase:attr.nimo_offer_phase, order_status:'unconfirmed'});
      formMessage(form, config.cartErrorText || 'Unable to continue. Check your cart before retrying; no payment was taken.');
      const region=form.querySelector('[data-nimo-form-error]');
      if(region){const link=document.createElement('a');link.href=config.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`;
        link.textContent=config.cartReviewText || 'Review cart';region.append(' ',link);}
    } finally { busy.delete(form); if (button) button.disabled=originalDisabled; }
  }
  async function checkout(form, node) {
    if (busy.has(node)) return;
    if (form && !form.reportValidity()) return;
    busy.add(node);
    try {
      const payload={};
      if (form) {
        const fd=new FormData(form),inputs=[...form.querySelectorAll('[name="updates[]"]')],updates={};
        for(const input of inputs){
          const value=Number(input.value),key=input.dataset.nimoLineKey;
          if(!key||key.length>256||key in updates||!Number.isInteger(value)||value<0||value>99)throw new Error('Invalid cart quantity/key');
          updates[key]=value;
        }
        if (inputs.length) {
          const live=await readCart(),keys=live.items.map(item=>item.key);
          if(keys.length!==inputs.length||keys.some(key=>!(key in updates)))throw new Error('Cart changed in another tab; refresh before checkout');
          payload.updates=updates; // Shopify line-item keys, never stale positional quantities.
        }
        if (fd.has('note')) payload.note=String(fd.get('note')); // Native cart note, never sent to analytics.
      }
      const ref=reference();payload.attributes=attributes(ref,null); // Re-check consent after asynchronous cart read.
      const cart=await post(route('cartUpdateUrl','cart/update'),payload);
      if (cart.item_count === 0) throw new Error('Cart is empty');
      emit('nimo_checkout_attempt',{checkout_attempt_id:ref,checkout_transport:'shopify_cart_attributes',order_status:'unconfirmed'});
      analytics()?.flush();
      window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch { formMessage(form || node.closest('section') || document.body,
      config.cartErrorText || 'Unable to continue. Please retry from the cart.'); }
    finally { busy.delete(node); }
  }
  document.addEventListener('submit', event => {
    const form=event.target;
    if (form.matches('[data-nimo-product-form]')) { event.preventDefault(); add(form); }
    else if (event.submitter?.name === 'checkout' || event.submitter?.matches('[data-nimo-checkout]')) {
      event.preventDefault(); checkout(form,event.submitter);
    }
  });
  document.addEventListener('click', event => {
    const node=event.target.closest?.('a[data-nimo-checkout]');
    if (node) { event.preventDefault(); checkout(null,node); }
  });
  document.addEventListener('change', event => {
    const select=event.target.closest?.('[data-nimo-variant]');if (select) variantChanged(select);
    const node=event.target.closest?.('[data-nimo-tier]');
    if (node && node.checked) emit('nimo_sku_selected',{tier_id:node.dataset.nimoTier,sku_id:node.dataset.nimoSku});
  });
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-nimo-variant]').forEach(variantChanged);
  });
  document.addEventListener('visitorConsentCollected', () => {
    if (!permitted('marketing')) { try { sessionStorage.removeItem(ATTR_KEY); } catch {} }
    if (!permitted('analytics')) { try { sessionStorage.removeItem(ATTEMPT_KEY); } catch {} }
    captureAttribution();
    // Best-effort clear cart linkage on revocation; checkout always rechecks permission.
    if (!permitted('analytics') || !permitted('marketing')) {
      post(route('cartUpdateUrl','cart/update'), {attributes:attributes(permitted('analytics') ? reference() : '',null)}).catch(() => {});
    }
  });
  document.addEventListener('nimo:privacy-ready', captureAttribution);
  window.addEventListener('pageshow',event=>{
    if (!event.persisted) return;
    for (const [form,disabled] of directPending) {
      busy.delete(form);const button=form.querySelector('[type="submit"]');if (button) button.disabled=disabled;
    }
    directPending.clear();
  });
  window.NimoCommerce=Object.freeze({captureAttribution,attributes,reference,route});
  try {captureAttribution();} catch {}
})();



